<template>
  <v-container class="helmi-commission-agreement">
    <template v-if="!loading">
      <template v-if="showAgreement">
        <h1 class="text-h4 mt-8 mb-4">
          Toimeksiantosopimus
        </h1>
        <helmi-summary :property="property"/>

        <helmi-form
          id="commissionAgreementForm"
          :active-index="0"
          :items="schema"
          :options="options"
        />
      </template>
      <template v-if="showAdditional">
        <h2 class="text-h5 mt-6 mb-2">
          Selostusliitteen tarkentavat kysymykset
        </h2>
        <helmi-form
          id="additionalQuestionsForm"
          :items="additionalSchema"
          :options="options"
          color="secondary"
        />
      </template>

      <div v-if="side !== 'additional'">
        <v-container>
          <v-checkbox
            v-model="includeCancel"
            label="Sisällytä etämyynnin peruuttamislomake"
          />
        </v-container>

        <canceling v-if="includeCancel" :canceling="cancelSchema"/>
      </div>

      <helmi-form-actions>
        <v-dialog
          v-model="resetFormDialog"
          persistent
          max-width="400"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              :disabled="signatures && signatures.length > 0"
              color="primary"
              outlined
              v-on="on"
            >
              Tyhjennä
            </v-btn>
          </template>
          <v-card>
            <v-card-title class="text-h5">
              Oletko varma?
            </v-card-title>
            <v-card-text class="pt-3">
              Menetät kaikki toimeksiantosopimukselle ja selostusliitteen tarkentaviin kysymyksiin
              syöttämäsi tiedot.
            </v-card-text>
            <v-card-actions>
              <v-spacer/>
              <v-btn text @click="resetFormDialog = false">
                Peruuta
              </v-btn>
              <v-btn
                color="primary"
                text
                @click="resetFormAndSchema"
              >
                Luo uudelleen
              </v-btn>
              <v-btn
                color="primary"
                text
                @click="resetForm"
              >
                Tyhjennä
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <helmi-detect-changes :form="form"/>

        <v-spacer/>
        <v-btn
          color="primary"
          :disabled="signatures && signatures.length > 0"
          @click="submitForm"
        >
          Tallenna ja jatka
        </v-btn>
      </helmi-form-actions>
    </template>
    <helmi-loader
      background-color="grey lighten-4"
      :loading="loading"
    />
  </v-container>
</template>

<script>
/* eslint-disable no-param-reassign */

import { mapActions, mapState } from 'vuex';
import moment from 'moment';
import _ from 'lodash';
import { mergeDeep, EventBus } from '@/utils';
import options from '@/components/formElements/options';

export default {
  name: 'CommissionAgreement',

  components: {
    'helmi-form': () => import('@/components/formElements/Form'),
    'helmi-loader': () => import('@/components/formElements/Loader'),
    'helmi-form-actions': () => import('@/components/formElements/Actions'),
    'helmi-summary': () => import('@/components/CommissionAgreementSummary'),
    'helmi-detect-changes': () => import('@/components/CommissionAgreementChangesDetected'),
    canceling: () => import('@/components/commissionAgreement/Canceling'),
  },

  data: () => ({
    changesDetected: false,
    additionalSchema: {},
    additionalDetails: {},
    agreement: {},
    includeCancel: true,
    loading: true,
    options,
    property: {},
    resetFormDialog: false,
    schema: {},
    cancelSchema: {},
    valid: true,
  }),

  computed: {
    ...mapState({
      userDetails: state => state.userDetails,
      commissionAgreements: state => state.commissionAgreement.byId,
    }),

    side() {
      return this.$route.params.side || '';
    },

    showAgreement() {
      return (!this.$route.params.side || this.$route.params.side === 'agreement');
    },

    showAdditional() {
      return (!this.$route.params.side || this.$route.params.side === 'additional');
    },

    id() {
      return this.$route.params.id;
    },

    form() {
      if (!this.agreement) return {};
      if (!this.property) return {};
      return {
        created: moment(new Date()).format('YYYY-MM-DD H:mm:ss'),
        property: {
          ...this.property,
          agentName: this.userDetails.nimi,
          agentEmail: this.userDetails.email,
          agentPhone: this.userDetails.puhelin,
          agentId: this.userDetails.id,
        },
        agreement: this.schema,
        additionalDetails: this.additionalSchema,
      };
    },

    signatures() {
      if (
        this.commissionAgreements
        && this.commissionAgreements[this.id]
        && this.commissionAgreements[this.id].signatures
      ) {
        return this.commissionAgreements[this.id].signatures;
      }
      return [];
    },

    canceling() {
      if (this.commissionAgreements && this.commissionAgreements[this.id]) {
        return this.commissionAgreements[this.id].cancel;
      }
      return null;
    },

    isShare() {
      return this.property.osakevaikiinteisto === '1';
    },

    isRental() {
      return this.property.onkovuokrakohde === '2';
    },

    assignmentType() {
      if (!Object.keys(this.property).length) return null;

      if (this.isRental && this.isShare) return 'rentalCondominium';
      if (this.isRental) return 'rentalProperty';
      if (this.isShare) return 'condominium';
      return 'property';
    },
  },

  watch: {
    /**
     * Initialize the schema download when the property type is awailable
     */
    assignmentType: {
      async handler(type) {
        if (!type) return;

        await this.loadSchema(type);
      },
      immediate: true,
    },
  },

  async mounted() {
    this.$bus.$on('RELOAD_COMMISSION_AGREEMENT', this.getAgreementData);
    this.$bus.$on('RESET_COMMISSION_AGREEMENT', this.reloadSchema);
    this.$bus.$on('POPULATE_COMMISSION_AGREEMENT', this.getPropertyAndPopulate);
    await this.getAgreementData();
  },

  methods: {
    ...mapActions({
      createAgreement: 'commissionAgreement/create',
      getAgreement: 'commissionAgreement/getOne',
      getProperty: 'properties/getOne',
      setCurrentProperty: 'setCurrentProperty',
    }),

    async getAgreementData() {
      console.warn('reloading');
      this.schema = { ...this.agreement };
      this.loading = true;
      this.setCurrentProperty(this.id);
      const parameters = {};
      if (this.side) {
        parameters.side = this.side;
      }
      const response = await this.getAgreement({ id: this.id, parameters });
      if (response && response.agreement) {
        this.includeCancel = !!response.cancel;
        this.agreement = response.agreement;
        this.schema = { ...this.agreement };
        this.additionalDetails = response.additionalDetails;
        this.property = response.property;
      } else {
        this.property = await this.getProperty({ id: this.id });
      }

      this.loading = false;
    },

    /**
     * Load schemas based on the assignment type
     *
     * @param {String} type - the assignment type (property | rentalProperty | condiminium)
     */
    async loadSchema(type) {
      this.schema = {};
      this.additionalSchema = {};
      this.cancelSchema = {};
      const promises = [
        import(`./data/schema.${type}.js`),
        import(`./data/additional.schema.${type}.js`),
        import('./data/cancel.js'),
      ];

      let schema = null;
      let additionalSchema = null;
      let cancelSchema = null;

      [
        { default: schema },
        { default: additionalSchema },
        { default: cancelSchema },
      ] = await Promise.all(promises);

      this.schema = _.cloneDeep(this.agreement);
      this.additionalSchema = mergeDeep(additionalSchema, this.additionalDetails);
      this.cancelSchema = _.cloneDeep(cancelSchema);

      if (!Object.keys(this.agreement).length) {
        this.schema = mergeDeep(schema, this.agreement);
        await this.populateSchema();
      }

      if (this.canceling) {
        this.cancelSchema = _.cloneDeep(this.canceling);
      }
    },

    async getPropertyAndPopulate() {
      this.property = await this.getProperty({ id: this.id });
      await this.populateSchema();
      const side = this.$route.params.side || '';
      await this.createAgreement({
        data: {
          id: this.id,
          form: { ...this.form, property: this.property },
          side,
        },
      });
      await this.getAgreementData();
    },

    async populateSchema() {
      const {
        vhinta,
        hinta,
        kuukausivuokra,
        vakuus,
        vapautuminen,
        valitysprosentti,
        valityssumma,
        toimeksiannonalku,
        toimeksiannonloppu,
        vuokrankorottaminen,
        vuokravapautuminen,
        vuokravapautuminenpvm,
        asiakirjakulut,
        markkinointikulut,
        valokuvaus,
      } = this.property;

      if (this.isShare && !this.isRental) {
        if (vhinta !== '0.00') {
          this.setValueFromProperty(
            this.schema.priceAndConsiderations.children.debtFreeSellingPrice,
            vhinta,
          );
        }
      }

      if (this.isRental) {
        if (kuukausivuokra !== '0.00') {
          this.setValueFromProperty(
            this.schema.rentalTerms.children.rent,
            kuukausivuokra,
          );
        }
        if (vuokrankorottaminen !== '') {
          this.setValueFromProperty(
            this.schema.rentalTerms.children.rentIncrease,
            vuokrankorottaminen,
          );
        }
        if (vakuus !== '0.00') {
          this.setValueFromProperty(
            this.schema.rentalTerms.children.rentDeposit,
            vakuus,
          );
        }
      }

      if (!this.isShare && !this.isRental) {
        if (hinta !== '0.00') {
          this.setValueFromProperty(
            this.schema.priceAndConsiderations.children.debtFreeSellingPrice,
            hinta,
          );
        }
      }

      if (!this.isRental && vapautuminen !== '') {
        this.setValueFromProperty(this.schema.rent.children.releaseDate, vapautuminen);
      } else {
        const releases = {
          0: 'Heti vapaa',
          1: 'Vapautuu sopimuksen mukaan',
          2: 'Vapautuu pvm',
        };
        this.setValueFromProperty(this.schema.rent.children.releaseDate,
          releases[vuokravapautuminen]);
      }

      if (vuokravapautuminen === '2') {
        this.setValueFromProperty(this.schema.rent.children.releaseDate_1,
          vuokravapautuminenpvm);
      }

      if (valitysprosentti !== '0.00') {
        this.setValueFromProperty(
          this.schema.brokerageAndPayment.children.percentage,
          valitysprosentti,
        );
      }

      if (valityssumma !== '0.00') {
        this.setValueFromProperty(this.schema.brokerageAndPayment.children.fixedFee, valityssumma);
      }

      if (valokuvaus !== '0.00') {
        this.setValueFromProperty(this.schema.brokerageAndPayment.children.photography,
          valokuvaus);
      }

      if (markkinointikulut !== '0.00') {
        this.setValueFromProperty(this.schema.brokerageAndPayment.children.marketing,
          markkinointikulut);
      }

      if (asiakirjakulut !== '0.00') {
        this.setValueFromProperty(this.schema.brokerageAndPayment.children.documents,
          asiakirjakulut);
      }

      this.setValueFromProperty(
        this.schema.periodOfValidity.children.timeLimitedFrom,
        toimeksiannonalku,
      );

      if (toimeksiannonalku === toimeksiannonloppu) {
        this.setValueFromProperty(
          this.schema.periodOfValidity.children.typeOfAgreement,
          'Toistaiseksi voimassa oleva',
        );

        this.setValueFromProperty(
          this.schema.periodOfValidity.children.untilFurtherNoticeFrom,
          toimeksiannonalku,
        );
      } else {
        this.setValueFromProperty(
          this.schema.periodOfValidity.children.timeLimitedTo,
          toimeksiannonloppu,
        );
      }
    },

    async resetForm() {
      EventBus.$emit('FORM_RESET');
      this.resetFormDialog = false;
      this.property = await this.getProperty({ id: this.id });
    },

    async resetFormAndSchema() {
      await this.reloadSchema();
      await this.resetForm();
    },

    async reloadSchema() {
      this.agreement = {};
      this.additionalDetails = {};
      this.property = await this.getProperty({ id: this.id });
      EventBus.$emit('FORM_RESET');
      await this.loadSchema(this.assignmentType);
    },

    /**
     * Set value to item from property
     * Set propertyValue to reset any changes to on resetForm
     */
    setValueFromProperty(item, value) {
      item.value = value;
      item.propertyValue = value;
    },

    async submitForm() {
      const side = this.$route.params.side || '';

      await this.createAgreement({
        data: {
          id: this.id,
          form: this.form,
          cancel: this.includeCancel ? this.cancelSchema : '',
          side,
        },
      });
      this.$router.push({ name: 'commissionAgreementPreview', params: this.$route.params });
    },
  },
};
</script>

<style lang="scss">
.helmi-commission-agreement {
  position: relative;
}

.assignment-form {
  .v-text-field .v-label,
  .v-text-field--filled > .v-input__control > .v-input__slot {
    background-color: transparent !important;
  }

  .v-expansion-panel--active .v-expansion-panel-content {
    padding-top: 24px;
  }
}
</style>
